import { zodResolver } from '@hookform/resolvers/zod';
import { type ReactElement, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { AddressUS, DatePicker, Input, Name, PhoneInput } from '@/components/DesignLibrary/Atoms';
import { addressSchema } from '@/components/DesignLibrary/Atoms/AddressUS';
import Button from '@/components/DesignLibrary/Button';
import parseMemberDateOfBirthForPicker from '@/helpers/parseMemberDateOfBirthForPicker';
import useNoodleApi from '@/hooks/useNoodleApi';
import { useUserProfile } from '@/providers/Auth';
import { creatorMembers } from '@/tsClient';
import type * as ApiModels from '@/typings/api-models';

import s from './CreditReportModal.module.scss';

const schema = z.object({
  address: addressSchema(true),
  dob: z
    .union([z.date({ invalid_type_error: 'Invalid date format' }), z.null()])
    .refine((val) => val !== null, { message: 'Date of birth is required' }),
  name: z.object({
    firstName: z.string().nonempty('First Name is required'),
    lastName: z.string().nonempty('Last Name is required'),
    middleName: z.string().optional(),
  }),
  nationalPhoneNumber: z.string(),
  phoneNumber: z.string().nonempty('Phone Number is required'),
  ssn: z
    .string()
    .nonempty('SSN is required')
    .length(9, 'SSN must be exactly 9 digits')
    .regex(/^\d{9}$/, 'SSN must contain only digits'),
});

type FormData = z.infer<typeof schema>;
type Customer = Pick<ApiModels.Person, 'id' | 'name' | 'email' | 'contactPhoneNumber'>;

type CreditFormProps = {
  buttonLabel?: string;
  creatorSlug?: string;
  userInfoData?: Customer | null;
  handleUserVerification: ({ email, userInfo }: { email: string; userInfo: ApiModels.CreditReportUserInfo }) => void;
};

const CreditReportForm = ({
  buttonLabel,
  creatorSlug,
  userInfoData,
  handleUserVerification,
}: CreditFormProps): ReactElement => {
  const userProfile = useUserProfile();
  const profile = userInfoData || userProfile;

  const userNames = profile?.name?.split(' ') || [];

  const firstName = userNames.length > 0 ? userNames[0] : '';
  const lastName = userNames.length > 1 ? userNames[userNames.length - 1] : '';

  const contactPhoneNumber = profile?.contactPhoneNumber || '';

  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [privateProfileData, setPrivateProfileData] = useState<Awaited<
    ReturnType<typeof creatorMembers.getMemberPrivateProfile>
  > | null>(null);

  const { getData: getMemberPrivateProfileFn } = useNoodleApi(creatorMembers.getMemberPrivateProfile, {
    toastOnError: false,
  });

  useEffect(() => {
    if (creatorSlug && userInfoData?.id) {
      getMemberPrivateProfileFn({ creatorSlug, personId: userInfoData.id })
        .then((response) => {
          if (response.data) {
            setPrivateProfileData(response.data);
          }
        })
        .catch(() => {
          // Silently fail - private profile may not exist
        });
    }
  }, [creatorSlug, userInfoData?.id]);

  const {
    control,
    handleSubmit,
    formState: { errors, touchedFields },
    setValue,
    trigger,
  } = useForm<FormData>({
    defaultValues: {
      address: {
        city: '',
        line1: '',
        line2: '',
        state: '',
        zip: '',
      },
      dob: undefined,
      name: {
        firstName,
        lastName,
        middleName: '',
      },
      nationalPhoneNumber: '',
      phoneNumber: contactPhoneNumber,
      ssn: '',
    },
    resolver: zodResolver(schema),
  });

  // Update form values when private profile data is loaded
  useEffect(() => {
    if (privateProfileData) {
      setValue('address', {
        city: privateProfileData.city ?? '',
        line1: privateProfileData.line1 ?? '',
        line2: privateProfileData.line2 ?? '',
        state: privateProfileData.state ?? '',
        zip: privateProfileData.zip ?? '',
      });
      if (privateProfileData.dateOfBirth) {
        const dobDate = parseMemberDateOfBirthForPicker(privateProfileData.dateOfBirth);
        if (dobDate) {
          setValue('dob', dobDate);
        }
      }
      if (privateProfileData.socialSecurityNumber) {
        // Strip dashes from SSN - form expects only digits
        const ssnDigitsOnly = privateProfileData.socialSecurityNumber.replace(/-/g, '');
        setValue('ssn', ssnDigitsOnly);
      }
    }
  }, [privateProfileData, setValue]);

  type AddressErrors = {
    line1?: { message?: string };
    line2?: { message?: string };
    city?: { message?: string };
    state?: { message?: string };
    zip?: { message?: string };
  };

  const getAddressError = (addressErrors: AddressErrors | undefined): string | undefined => {
    if (!addressErrors) return undefined;

    const errorPriority = ['line1', 'city', 'state', 'zip'] as const;

    const firstErrorKey = errorPriority.find((key) => addressErrors[key]?.message);

    return firstErrorKey ? addressErrors[firstErrorKey]?.message : undefined;
  };

  const onSubmit = async (data: FormData): Promise<void> => {
    const isValid = await trigger();

    if (!isValid || phoneError) {
      return;
    }

    const formattedDob = data.dob ? data.dob.toISOString().split('T')[0] : '';

    const formattedPhoneNumber = data.nationalPhoneNumber.replace(/[()\s-]+/g, '');

    const userInfo = {
      city: data.address.city,
      dob: formattedDob,
      fname: data.name.firstName,
      lname: data.name.lastName,
      mname: data.name.middleName,
      mobile: formattedPhoneNumber,
      ssn: data.ssn,
      state: data.address.state,
      street1: data.address.line1,
      street2: data.address.line2,
      zip: data.address.zip,
    };

    handleUserVerification({
      email: profile?.email || '',
      userInfo,
    });
  };

  return (
    <form className={s.creditReportForm} onSubmit={handleSubmit(onSubmit)}>
      <Controller
        name='name'
        control={control}
        render={({ field }) => (
          <Name
            label='Full Name'
            placeholder=''
            value={field.value}
            error={{
              firstName: errors.name?.firstName?.message,
              lastName: errors.name?.lastName?.message,
            }}
            onChange={(newValue) => {
              setValue('name', newValue);
              trigger('name');
            }}
            onBlur={() => {
              field.onBlur();
              trigger('name');
            }}
          />
        )}
      />

      <Controller
        name='ssn'
        control={control}
        render={({ field }) => (
          <Input
            label='Social Security Number'
            type='text'
            optional={false}
            placeholder='Enter your social security number'
            value={field.value}
            error={errors.ssn?.message}
            onChange={(value) => {
              field.onChange(value);
              trigger('ssn');
            }}
            onBlur={() => {
              field.onBlur();
              trigger('ssn');
            }}
          />
        )}
      />

      <Controller
        name='dob'
        control={control}
        render={({ field }) => (
          <DatePicker
            label='Date Of Birth'
            max={new Date()}
            required={true}
            value={field.value ?? null}
            error={errors.dob?.message}
            selected={field.value ?? undefined}
            onChange={(date) => {
              field.onChange(date);
              trigger('dob');
            }}
            onBlur={() => {
              field.onBlur();
              trigger('dob');
            }}
          />
        )}
      />

      <Controller
        name='phoneNumber'
        control={control}
        render={({ field }) => (
          <PhoneInput
            id='phoneNumber'
            label='Cell Phone Number'
            placeholder='Enter your cell phone number'
            required={true}
            value={field.value}
            error={touchedFields.phoneNumber && errors.phoneNumber?.message ? errors.phoneNumber?.message : undefined}
            allowInvalidPhoneNumber
            onChange={(phoneNumber: string, _country: string, nationalPhoneNumber: string | undefined) => {
              setValue('nationalPhoneNumber', nationalPhoneNumber ?? '');
              field.onChange(phoneNumber);
              trigger('phoneNumber');
            }}
            onError={(error) => {
              setPhoneError(error);
            }}
            onBlur={() => {
              field.onBlur();
              trigger('phoneNumber');
            }}
          />
        )}
      />

      <Controller
        name='address'
        control={control}
        render={({ field }) => {
          const addressError = getAddressError(errors.address as AddressErrors);

          return (
            <AddressUS
              label='Address'
              description='Provide the address you used with your creditors within the past three months.'
              required={true}
              value={field.value}
              error={addressError}
              onChange={(newValue) => {
                setValue('address', newValue);
                trigger('address');
              }}
              onBlur={() => {
                field.onBlur();
                trigger('address');
              }}
            />
          );
        }}
      />
      {!userInfoData && (
        <p className='caption'>
          You understand that by clicking “Continue”, you are explicitly agreeing to and providing “written
          instructions” to StitchCredit under the Fair Credit Reporting Act to obtain my credit information from one or
          more of the three nationwide credit reporting agencies. Third-party sources, including your mobile carrier may
          be used to verify your identity. You authorize StitchCredit to obtain such information for you to confirm your
          identity, and, for as long as you are a member of StitchCredit, to provide you with your credit information.
          You may elect to terminate your account and this authorization at any time.
        </p>
      )}
      <Button variant='primary' size='md' fullWidth={true} type='submit'>
        {buttonLabel || 'Continue'}
      </Button>
    </form>
  );
};

export default CreditReportForm;
